import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, AlertTriangle, Sparkles } from 'lucide-react';

const MaintenanceScreen = ({ customMessage }) => {
  // Generate random positions once using useMemo to avoid re-renders
  const particles = useMemo(() => 
    [...Array(30)].map((_, i) => ({
      key: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      duration: 3 + Math.random() * 2,
      delay: Math.random() * 3
    })), []
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1b2a] to-[#0a1628] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Animated background particles */}
      {particles.map((particle) => (
        <motion.div
          key={particle.key}
          className="absolute w-1 h-1 bg-orange-400 rounded-full"
          style={{ left: `${particle.left}%`, top: `${particle.top}%` }}
          animate={{ opacity: [0, 1, 0], scale: [0, 1.5, 0] }}
          transition={{ duration: particle.duration, repeat: Infinity, delay: particle.delay }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full text-center relative z-10 px-4"
      >
        {/* Animated Icon */}
        <motion.div
          className="inline-block mb-6 sm:mb-8"
          animate={{ 
            rotate: [0, 10, -10, 0],
            y: [0, -10, 0]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500 rounded-full blur-3xl opacity-50 animate-pulse"></div>
            <div className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl flex items-center justify-center border-2 border-orange-500/30">
              <Wrench size={48} className="text-orange-400 sm:w-16 sm:h-16" />
            </div>
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-orange-400 via-red-500 to-orange-400 bg-clip-text text-transparent">
              Under Maintenance
            </span>
          </h1>
        </motion.div>

        {/* Custom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-6 sm:mb-8"
        >
          <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-xl rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-orange-500/20 shadow-2xl">
            <div className="flex items-center justify-center gap-3 mb-4">
              <AlertTriangle className="text-orange-400" size={20} />
              <h2 className="text-xl sm:text-2xl font-bold text-white">System Maintenance</h2>
            </div>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
              {customMessage || 'We are currently performing system maintenance to improve your experience. Please check back in a few moments.'}
            </p>
          </div>
        </motion.div>

        {/* Status Indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8"
        >
          <div className="bg-gray-800/40 backdrop-blur-md rounded-xl p-4 border border-gray-700/50">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-3xl mb-2"
            >
              🔧
            </motion.div>
            <p className="text-gray-400 text-sm">Upgrading</p>
          </div>
          <div className="bg-gray-800/40 backdrop-blur-md rounded-xl p-4 border border-gray-700/50">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              className="text-3xl mb-2"
            >
              ⚙️
            </motion.div>
            <p className="text-gray-400 text-sm">Optimizing</p>
          </div>
          <div className="bg-gray-800/40 backdrop-blur-md rounded-xl p-4 border border-gray-700/50">
            <motion.div
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-3xl mb-2"
            >
              🚀
            </motion.div>
            <p className="text-gray-400 text-sm">Coming Soon</p>
          </div>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mb-8"
        >
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-orange-500 via-red-500 to-orange-500"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              style={{ width: '50%' }}
            />
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-gray-500 text-sm"
        >
          <p className="mb-2">We appreciate your patience</p>
          <p className="flex items-center justify-center gap-2">
            <Sparkles size={16} className="text-orange-400" />
            <span>@CyborXxRobot</span>
            <Sparkles size={16} className="text-orange-400" />
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default MaintenanceScreen;
